import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { useTrackingEvent } from "@/hooks/useTrackingEvent";

import { POSTPONE_DURATION_MS } from "../constants";
import { useServiceWorkerUpdate } from "../hooks/useServiceWorkerUpdate";
import { AutoReloadScheduler } from "./AutoReloadScheduler";

type AppUpdate = {
  isUpdateAvailable: boolean;
  postponeUpdate: () => void;
  reloadNow: () => void;
};

const AppUpdateContext = createContext<AppUpdate | null>(null);

export const useAppUpdate = (): AppUpdate | null => {
  return useContext(AppUpdateContext);
};

export const ServiceWorkerUpdateProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const { applyUpdate, isUpdateAvailable } = useServiceWorkerUpdate();
  const [isPostponed, setIsPostponed] = useState(false);
  const { trackEvent } = useTrackingEvent();

  useEffect(() => {
    if (isUpdateAvailable) {
      trackEvent("AppUpdateAvailable");
    }
  }, [isUpdateAvailable, trackEvent]);

  useEffect(() => {
    if (!isPostponed) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsPostponed(false);
    }, POSTPONE_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [isPostponed]);

  const reloadNow = useCallback(() => {
    trackEvent("AppUpdateAccepted");
    applyUpdate();
  }, [applyUpdate, trackEvent]);

  const reloadWhenSafe = useCallback(() => {
    trackEvent("AppUpdateAutoReloaded");
    applyUpdate();
  }, [applyUpdate, trackEvent]);

  const postponeUpdate = useCallback(() => {
    trackEvent("AppUpdatePostponed");
    setIsPostponed(true);
  }, [trackEvent]);

  const isUpdatePending = isUpdateAvailable && !isPostponed;

  return (
    <AppUpdateContext.Provider value={{ isUpdateAvailable: isUpdatePending, postponeUpdate, reloadNow }}>
      {isUpdatePending ? <AutoReloadScheduler onReloadWhenSafe={reloadWhenSafe} /> : null}
      {children}
    </AppUpdateContext.Provider>
  );
};
